import React, { useEffect, useState } from "react"
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import Search from 'pages/Parts/Search'
import Pagination from 'pages/Parts/Pagination'

import { GET, PGET } from 'helpers/api'
import { SET_TABLE_FILTER } from "store/actions"
import { formatMoney } from "helpers/helpers"
import * as XLSX from 'xlsx'

function Index() {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	const URL = '/services/admin/api/product-pageList'

	const settings = useSelector(state => state.settings)
	const reduxTableFilter = useSelector(state => state.tableFilter)

	const [showModal, setShowModal] = useState(false)
	const [pageCount, setPageCount] = useState(0)
	const [totalCount, setTotalCount] = useState(0)

	const [searchValue, setSearchValue] = useState('')
	const [pageSize, setPageSize] = useState(20)

	const [data, setData] = useState([])
	const [statistics, setStatistics] = useState([])

	async function getData(fromSearch = false) {
		var filter = { size: pageSize }
		if (reduxTableFilter.page === 'products') {
			filter = { ...reduxTableFilter.filters, size: pageSize }
			if (!fromSearch) {
				if (reduxTableFilter?.filters?.search) {
					setSearchValue(reduxTableFilter?.filters?.search)
				}
			} else {
				setSearchValue('')
				filter.search = ''
			}
		}
		const response = await PGET('/services/admin/api/product-pageList', filter, { loader: true })
		if (response) {
			setTotalCount(Math.ceil(response.headers['x-total-count']))
			setPageCount(Math.ceil(response.headers['x-total-count'] / pageSize))
			setData(response.data)
		}
	}

	async function paginate(data) {
		const response = await PGET('/services/admin/api/product-pageList', { page: data.selected, size: pageSize, search: searchValue }, { loader: true })
		setData(response.data);
		dispatch(SET_TABLE_FILTER({
			'page': 'products',
			'filters': {
				'page': data.selected,
				'search': searchValue,
				'size': pageSize
			}
		}))
	}

	async function openModal() {
		const response = await GET('/services/admin/api/product-changed-list')
		setStatistics(response)
		setShowModal(true)
	}

	function closeModal() {
		setShowModal(false)
	}

	async function search(data, searchValue, total) {
		setData(data);
		dispatch(SET_TABLE_FILTER({
			'page': 'products',
			'filters': {
				'page': 0,
				'search': searchValue,
				'size': pageSize
			}
		}))
		setSearchValue(searchValue)
		setPageCount(Math.ceil(total / pageSize))
		if (searchValue.length > 0) {
			setPageCount(Math.ceil(total / pageSize))
		}
	}

	useEffect(() => {
		getData()
	}, [pageSize]) // eslint-disable-line react-hooks/exhaustive-deps

	function handlePageSizeChange(selectedOption) {
		if (selectedOption.value === 'all') {
			// Export all data directly and keep current page size selection
			exportAllToExcel();
			// Don't change the pageSize state, so the selector stays on current value
		} else {
			setPageSize(selectedOption.value)
		}
	}

	const pageSizeOptions = [
		{ value: 20, label: '20' },
		{ value: 50, label: '50' },
		{ value: 100, label: '100' },
		{ value: 500, label: '500' },
		{ value: 1000, label: '1000' },
		{ value: 'all', label: t('ALL') }
	]

	// Excel export function for current page
	function exportToExcel() {
		// Prepare data for Excel
		const excelData = data.map((item, index) => ({
			'№': index + 1,
			[t('NAME_OF_PRODUCT')]: item.name || '',
			[t('BARCODE')]: item.barcode || '',
			[t('CATEGORY')]: item.categoryName || '',
			[t('IMAGE2')]: item.imageUrl ? process.env.REACT_APP_PROD_SERVER_URL + item.imageUrl : t('NO_IMAGE')
		}))

		// Create workbook and worksheet
		const ws = XLSX.utils.json_to_sheet(excelData)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, t('PRODUCTS'))

		// Generate Excel file and download
		XLSX.writeFile(wb, `${t('PRODUCTS')}_${new Date().toLocaleDateString()}.xlsx`)
	}

	// Excel export function for all data
	async function exportAllToExcel() {
		try {
			// Get all data without pagination
			var filter = { size: totalCount } // Use total count to get all records
			if (reduxTableFilter.page === 'products') {
				filter = { ...reduxTableFilter.filters, size: totalCount }
				if (reduxTableFilter?.filters?.search) {
					filter.search = reduxTableFilter.filters.search
				}
			}
			
			const response = await PGET('/services/admin/api/product-pageList', filter, { loader: true })
			
			if (response && response.data) {
				// Prepare all data for Excel
				const excelData = response.data.map((item, index) => ({
					'№': index + 1,
					[t('NAME_OF_PRODUCT')]: item.name || '',
					[t('BARCODE')]: item.barcode || '',
					[t('CATEGORY')]: item.categoryName || '',
					[t('IMAGE2')]: item.imageUrl ? process.env.REACT_APP_PROD_SERVER_URL + item.imageUrl : t('NO_IMAGE')
				}))

				// Create workbook and worksheet
				const ws = XLSX.utils.json_to_sheet(excelData)
				const wb = XLSX.utils.book_new()
				XLSX.utils.book_append_sheet(wb, ws, t('PRODUCTS'))

				// Generate Excel file and download
				XLSX.writeFile(wb, `${t('PRODUCTS')}_${t('ALL')}_${new Date().toLocaleDateString()}.xlsx`)
			}
		} catch (error) {
			console.error('Error exporting all data:', error)
		}
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between mb-2">
				<h4 className="vertical-center">
					{t('PRODUCTS')} ({totalCount})
				</h4>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary me-2" onClick={exportToExcel}>
						<i className="uil uil-file-export me-2"></i>
						Excel
					</button>
					<Link className="btn-rounded btn btn-primary" to="/products-create">
						<i className="uil uil-plus me-2"></i>
						{t('CREATE')}
					</Link>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex mb-3">
						<div className="me-2">
							<Search url={URL} search={search} getData={getData} searchValue={searchValue}></Search>
						</div>
						<div className="me-2" style={{ minWidth: '120px' }}>
							<Select
								options={pageSizeOptions}
								value={pageSizeOptions.find(option => option.value === pageSize)}
								onChange={handlePageSizeChange}
								placeholder="Page Size"
								isSearchable={false}
							/>
						</div>
						<div>
							<button className="btn btn-outline-primary btn-wide" onClick={() => openModal()}>
								{t('STATISTICS')}
							</button>
						</div>
					</div>
					<div className="table-responsive">
						<table className="table table-striped mb-0">
							<thead>
								<tr>
									<th>{t('IMAGE2')}</th>
									<th>{t('NAME_OF_PRODUCT')}</th>
									<th className="text-center">{t('BARCODE')}</th>
									<th className="text-center">{t('CATEGORY')}</th>
									<th className="text-center">{t('ACTION')}</th>
								</tr>
							</thead>
							<tbody>
								{data.map((item, index) => (
									<tr className="tr-middle" key={index}>
										<td>
											{item.imageUrl ?
												<img src={process.env.REACT_APP_PROD_SERVER_URL + item.imageUrl} alt="react" width="50" height="50" />
												:
												<div className="table-no-image">
													<i className="uil uil-camera position-relative fz-20" style={{ 'top': '20%' }}></i>
												</div>
											}
										</td>
										<td>{item.name}</td>
										<td className="text-center">{item.barcode}</td>
										<td className="text-center">{item.categoryName}</td>
										<td>
											<div className="d-flex justify-content-center">
												<Link to={'/products-update/' + item.id}>
													<div className="table-action-button table-action-primary-button">
														<i className="uil-edit-alt"></i>
													</div>
												</Link>
											</div>
										</td>
									</tr>
								))
								}
							</tbody>
						</table>
					</div>

					<Pagination pageCount={pageCount} paginate={paginate} reduxPage={reduxTableFilter?.filters?.page}></Pagination>
				</div>
			</div>

			{/* MODAL */}
			<Modal show={showModal} animation={false} centered dialogClassName="update-modal-width" backdrop="static" onHide={() => closeModal()}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('STATISTICS')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<table className="table">
						<thead>
							<tr>
								<th>{t('LOGIN')}</th>
								<th>{t('CREATED')}</th>
								<th>{t('UPDATED')}</th>
							</tr>
						</thead>
						<tbody>
							{statistics.map((item, index) => (
								<tr key={index}>
									<td>{item.login}</td>
									<td>{formatMoney(item.createdCount, 0)}</td>
									<td>{formatMoney(item.updatedCount, 0)}</td>
								</tr>
							))
							}
						</tbody>
					</table>
				</Modal.Body>
			</Modal>
			{/* MODAL */}
		</>
	)
}

export default Index
